/**
 * Which of the four kinds a base is.
 *
 *   legacy               — its own `@games/<game>` package; `version` documents
 *                          holding a `gameConfig`, previewed with applyGameConfig.
 *   versionedPlayEngine  — the shared playEngine; `versionPlayEngine` documents
 *                          holding a `playEngineConfig`, plus the media library.
 *   snapshotEditor       — the shared playEngine reached through agentContainer;
 *                          snapshot FILES in a flow's pool, no version documents.
 *   pe3d                 — PlayEngine3D: `pe3dVariant` documents, each naming its
 *                          own game and release; the base (`pe3d: true`) is a
 *                          shelf. Has its own list, preview and editor
 *                          (components/pe3d/) and never carries
 *                          playEngineInstance; checked first all the same.
 *
 * Ask this rather than reading `selectedBase.playEngineInstance` directly: that
 * flag is true for BOTH engine kinds, so on its own it never tells a
 * versionedPlayEngine base from a snapshot editor. The snapshot editor check
 * therefore comes first, through isSnapshotEditorBase so the legacy
 * `ecommEditor` / boolean forms count exactly as the editor counts them.
 *
 * Mirrors src/server/controller/base/baseKind.js; keep the two agreeing.
 */

import { isSnapshotEditorBase as hasSnapshotEditor } from "./snapshotEditorApi";

export const BASE_KINDS = Object.freeze({
  LEGACY: "legacy",
  VERSIONED_PLAY_ENGINE: "versionedPlayEngine",
  SNAPSHOT_EDITOR: "snapshotEditor",
  PE3D: "pe3d",
});

/**
 * @param {object|null|undefined} base
 * @returns {"legacy"|"versionedPlayEngine"|"snapshotEditor"|"pe3d"|null}  null
 *   when no base is selected
 */
export function getBaseKind(base) {
  if (!base) return null;
  if (base.pe3d === true) return BASE_KINDS.PE3D;
  if (hasSnapshotEditor(base)) return BASE_KINDS.SNAPSHOT_EDITOR;
  if (base.playEngineInstance === true) return BASE_KINDS.VERSIONED_PLAY_ENGINE;
  return BASE_KINDS.LEGACY;
}

export const isLegacyBase = (base) => getBaseKind(base) === BASE_KINDS.LEGACY;
export const isVersionedPlayEngineBase = (base) => getBaseKind(base) === BASE_KINDS.VERSIONED_PLAY_ENGINE;
export const isPe3dBase = (base) => getBaseKind(base) === BASE_KINDS.PE3D;
